import React, { useState } from 'react';
import { Search, List, Network, ChevronDown, ChevronRight, Eye, Box, Layers, Star } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';
import {
  extractDegreePrefix,
  calculateScore,
  cleanQueryForKeywords,
  extractWordsWithMetadata,
  SEARCH_CONFIG,
  getPrefixVariant
} from '../utils/rncpSearchUtils';

const RncpTest = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchType, setSearchType] = useState('intitule'); // 'intitule' ou 'rncp'
  const [certifications, setCertifications] = useState([]);
  const [allCertifications, setAllCertifications] = useState([]); // ✅ TOUS les résultats (même score < 20)
  const [selectedCert, setSelectedCert] = useState(null);
  const [certDetails, setCertDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('list'); // 'list' ou 'graph'
  const [expandedBlocks, setExpandedBlocks] = useState({});
  const [showAllResults, setShowAllResults] = useState(false);
  const [showLowScoreResults, setShowLowScoreResults] = useState(false); // ✅ Afficher résultats score < 20

  // ⚠️ FONCTIONS DE RECHERCHE MAINTENANT DANS utils/rncpSearchUtils.js
  // Les fonctions extractDegreePrefix et calculateScore sont importées depuis le fichier utilitaire

  // Fonction de scoring côté client (alias vers la fonction importée)
  const calculateClientScore = (query, title, rncpCode = '', isActive = true) => {
    return calculateScore(query, title, rncpCode, isActive);
  };

  // Fonction fuzzy match conservée localement (pas dans utils pour l'instant)
  const fuzzyMatch = (str1, str2) => {
    const normalizeStr = (str) => str.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const s1 = normalizeStr(str1);
    const s2 = normalizeStr(str2);

    const maxLen = Math.max(s1.length, s2.length);
    if (maxLen === 0) return 100;

    // Calcul simple de distance (peut utiliser levenshteinDistance de utils si besoin)
    let matches = 0;
    for (let i = 0; i < Math.min(s1.length, s2.length); i++) {
      if (s1[i] === s2[i]) matches++;
    }
    return Math.round((matches / maxLen) * 100);
  };


  // Recherche de certifications (DB + API combinées avec scoring)
  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      toast.error('Veuillez entrer un code RNCP ou un intitulé');
      return;
    }

    setLoading(true);
    setShowAllResults(false); // Réinitialiser l'affichage à top 1 résultat
    let loadingToast = null;
    let dbResults = [];
    let apiResults = [];

    try {
      // Étape 1: Utiliser la recherche intelligente avec scoring
      const smartSearchResponse = await api.get('/rncp/smart-search', {
        params: {
          q: searchTerm,
          minScore: 35, // Équilibre entre précision et rappel
          maxResults: 50
        }
      });

      if (smartSearchResponse.data.success && smartSearchResponse.data.data.certifications?.length > 0) {
        dbResults = smartSearchResponse.data.data.certifications;
        console.log('📊 Stats recherche locale:', smartSearchResponse.data.data.stats);
      }

      // Étape 2: Chercher aussi via l'API Kit Apprentissage (RECHERCHE MULTIPLE)
      loadingToast = toast.loading('Recherche dans l\'API externe...');

      const apiSearchTerm = searchTerm;
      console.log(`\n🔍 Recherche RNCP pour: "${apiSearchTerm}"`);

      // ✅ Extraire le préfixe de diplôme DÈS le début
      const { prefix, coreQuery } = extractDegreePrefix(apiSearchTerm);

      if (prefix) {
        console.log(`  📌 Préfixe détecté: "${prefix}"`);
        console.log(`  📌 Requête core: "${coreQuery}"`);
      }

      // Déterminer si c'est un code RNCP (numérique) ou un intitulé
      const isRncpCode = /^\d+$/.test(apiSearchTerm.trim());

      try {
        let allApiResponses = [];

        if (isRncpCode) {
          // Recherche par code RNCP exact
          const apiResponse = await api.get(`/kit-apprentissage/enriched/${apiSearchTerm}`);
          allApiResponses = [{ ...apiResponse, searchType: 1 }];
        } else {
          // RECHERCHES MULTIPLES pour intitulé

          // RECHERCHE 1 : Titre complet
          console.log(`  🔍 [TYPE 1] Recherche avec titre complet: "${apiSearchTerm}"`);
          const searchPromises = [
            api.get(`/kit-apprentissage/search/intitule`, { params: { q: apiSearchTerm, limit: 100 } })
              .then(data => ({ ...data, searchType: 1 }))
              .catch(() => ({ data: { success: false, data: [] }, searchType: 1 }))
          ];

          // RECHERCHE 2 : Sans le préfixe complet
          if (prefix && coreQuery && coreQuery !== apiSearchTerm) {
            console.log(`  🔍 [TYPE 2] Recherche sans préfixe "${prefix}": "${coreQuery}"`);
            searchPromises.push(
              api.get(`/kit-apprentissage/search/intitule`, { params: { q: coreQuery, limit: 100 } })
                .then(data => ({ ...data, searchType: 2 }))
                .catch(() => ({ data: { success: false, data: [] }, searchType: 2 }))
            );
          }

          // RECHERCHE 3 : Mots-clés spécifiques (jusqu'à 3 mots)
          // ✅ Utilise les constantes partagées depuis rncpSearchUtils
          const { stopWords, genericWords } = SEARCH_CONFIG;

          // ✅ Nettoyer : tirets → espaces, ponctuation → supprimée
          const queryForKeywords = (prefix ? coreQuery : apiSearchTerm)
            .replace(/-/g, ' ')                    // tirets → espaces
            .replace(/[,;:.!?()]/g, ' ')           // virgules, points, etc. → espaces
            .replace(/\s+/g, ' ')                  // espaces multiples → 1 espace
            .trim();

          const words = queryForKeywords.split(' ')
            .filter(w => w.length > 3 && !stopWords.includes(w.toLowerCase()))
            .map((w, index) => ({
              word: w,
              isGeneric: genericWords.includes(w.toLowerCase()),
              length: w.length,
              originalIndex: index // ✅ Conserver l'ordre original
            }));

          // ✅ Filtrer les mots spécifiques SANS trier (garder l'ordre original)
          const specificWords = words.filter(w => !w.isGeneric);

          // ❌ TYPE 3 : 3 mots spécifiques (DÉSACTIVÉ - peu efficace selon tests utilisateur)
          // Résultats tests : 0 formations trouvées
          /*
          if (specificWords.length >= 3) {
            const keyWords = specificWords.slice(0, 3).map(w => w.word).join(' ');
            if (keyWords && keyWords !== apiSearchTerm && keyWords !== coreQuery) {
              console.log(`  🔍 [TYPE 3] Recherche mots-clés spécifiques (3 mots): "${keyWords}"`);
              searchPromises.push(
                api.get(`/kit-apprentissage/search/intitule`, { params: { q: keyWords, limit: 100 } })
                  .then(data => ({ ...data, searchType: 3 }))
                  .catch(() => ({ data: { success: false, data: [] }, searchType: 3 }))
              );
            }
          }
          */

          // ✅ TYPE 5 : Seulement les 2 premiers mots (souvent plus efficace avec l'API)
          if (specificWords.length >= 2) {
            const keyWords2 = specificWords.slice(0, 2).map(w => w.word).join(' ');
            if (keyWords2 && keyWords2 !== apiSearchTerm && keyWords2 !== coreQuery) {
              console.log(`  🔍 [TYPE 5] Recherche mots-clés spécifiques (2 mots): "${keyWords2}"`);
              searchPromises.push(
                api.get(`/kit-apprentissage/search/intitule`, { params: { q: keyWords2, limit: 100 } })
                  .then(data => ({ ...data, searchType: 5 }))
                  .catch(() => ({ data: { success: false, data: [] }, searchType: 5 }))
              );
            }
          }

          // ✅ TYPE 6 : Recherche avec préfixe + 1 mot spécifique (pour mots simples comme "cybersécurité")
          // Ex: "BTS - cybersécurité" → recherche "BTS cybersécurité"
          if (prefix && specificWords.length >= 1) {
            const firstWord = specificWords[0].word;
            const queryWithPrefix = `${prefix} ${firstWord}`;
            console.log(`  🔍 [TYPE 6] Recherche préfixe + 1er mot: "${queryWithPrefix}"`);
            searchPromises.push(
              api.get(`/kit-apprentissage/search/intitule`, { params: { q: queryWithPrefix, limit: 100 } })
                .then(data => ({ ...data, searchType: 6 }))
                .catch(() => ({ data: { success: false, data: [] }, searchType: 6 }))
            );
          }

          // ✅ TYPE 7 : Recherche avec variante de préfixe (ex: "Titre professionnel" → aussi chercher avec "TP")
          if (prefix) {
            const prefixVariant = getPrefixVariant(prefix);
            if (prefixVariant && prefixVariant !== prefix.toUpperCase()) {
              const queryWithVariant = `${prefixVariant} ${coreQuery}`;
              console.log(`  🔍 [TYPE 7] Recherche avec variante de préfixe: "${prefix}" → "${prefixVariant}" : "${queryWithVariant}"`);
              searchPromises.push(
                api.get(`/kit-apprentissage/search/intitule`, { params: { q: queryWithVariant, limit: 100 } })
                  .then(data => ({ ...data, searchType: 7 }))
                  .catch(() => ({ data: { success: false, data: [] }, searchType: 7 }))
              );
            }
          }

          // ❌ TYPE 4 : Sans première lettre (DÉSACTIVÉ - peu efficace selon tests utilisateur)
          // Résultats tests : 3 formations trouvées (+2 uniques)
          // Performance insuffisante par rapport aux autres types
          /*
          if (coreQuery) {
            const coreWords = coreQuery.split(' ').filter(w => w.length > 0);
            if (coreWords.length > 0 && coreWords[0].length > 1) {
              const firstWordWithoutFirstLetter = coreWords[0].substring(1);
              const queryWithoutFirstLetter = [firstWordWithoutFirstLetter, ...coreWords.slice(1)].join(' ');

              if (queryWithoutFirstLetter && queryWithoutFirstLetter.length > 3) {
                console.log(`  🔍 [TYPE 4] Recherche sans 1ère lettre: "${queryWithoutFirstLetter}"`);
                searchPromises.push(
                  api.get(`/kit-apprentissage/search/intitule`, { params: { q: queryWithoutFirstLetter, limit: 100 } })
                    .then(data => ({ ...data, searchType: 4 }))
                    .catch(() => ({ data: { success: false, data: [] }, searchType: 4 }))
                );
              }
            }
          }
          */

          allApiResponses = await Promise.all(searchPromises);
        }

        // Combiner et dédupliquer les résultats de toutes les recherches
        let allApiCerts = [];
        const seenRncps = new Map(); // Map RNCP → searchType

        allApiResponses.forEach(response => {
          if (response.data?.success && response.data?.data) {
            const certs = Array.isArray(response.data.data)
              ? response.data.data
              : [response.data.data];

            certs.forEach(cert => {
              const rncpCode = cert.rncp || cert.code_rncp;
              if (rncpCode && !seenRncps.has(rncpCode)) {
                seenRncps.set(rncpCode, response.searchType);
                allApiCerts.push({
                  ...cert,
                  searchType: response.searchType // ✅ Ajouter le type
                });
              }
            });
          }
        });

        console.log(`  📊 Total résultats combinés: ${allApiCerts.length}`);

        // ✅ DEBUG : Chercher si RNCP 39063 est dans les résultats
        const target39063 = allApiCerts.find(cert => {
          const code = (cert.rncp || cert.code_rncp || '').replace('RNCP', '');
          return code === '39063';
        });
        if (target39063) {
          console.log(`  ✅ RNCP 39063 trouvé dans les résultats: "${target39063.intitule_diplome || target39063.intitule}" (TYPE ${target39063.searchType})`);
        } else {
          console.log(`  ❌ RNCP 39063 NOT FOUND dans les ${allApiCerts.length} résultats`);
        }

        // ✅ DEBUG : Lister TOUS les résultats avec "negociateur"
        const negociateurResults = allApiCerts.filter(cert => {
          const title = (cert.intitule_diplome || cert.intitule_rncp || cert.intitule || '').toLowerCase();
          return title.includes('negociateur');
        });
        if (negociateurResults.length > 0) {
          console.log(`  📋 ${negociateurResults.length} résultat(s) avec "negociateur":`);
          negociateurResults.forEach((cert, i) => {
            const code = (cert.rncp || cert.code_rncp || '').replace('RNCP', '');
            console.log(`    [${i}] RNCP ${code} - "${cert.intitule_diplome || cert.intitule}" (TYPE ${cert.searchType})`);
          });
        }

        if (allApiCerts.length > 0) {
          const apiCerts = allApiCerts;

          // IMPORTANT: Appliquer le scoring sur les résultats API
          const allScoredResults = apiCerts.map((cert, index) => {
            const title = cert.intitule_diplome || cert.intitule_rncp || cert.intitule || 'Sans titre';

            // Détecter si le RNCP est inactif
            const isInactive = cert.etat_fiche && (
              cert.etat_fiche.toLowerCase().includes('inactif') ||
              cert.etat_fiche.toLowerCase().includes('inactive')  ||
              cert.etat_fiche === 'Inactive'
            );

            // Calculer le score avec pénalité pour les inactifs
            const score = calculateClientScore(searchTerm, title, cert.rncp || cert.code_rncp || '', !isInactive);

            // DEBUG: Log des 5 premiers résultats ET des résultats avec score >= 80
            if (index < 5 || score >= 80) {
              const searchTypeLabel = cert.searchType ? `[TYPE ${cert.searchType}]` : '';
              console.log(`🔍 ${searchTypeLabel} [${index}] "${title.substring(0, 50)}" | Score: ${Math.round(score)} | RNCP: ${cert.rncp || cert.code_rncp}`);
            }

            return {
              id: `api_${cert.rncp || cert.code_rncp}_${index}_${title}`.replace(/\s+/g, '_'),
              rncpCode: (cert.rncp || cert.code_rncp || '').replace('RNCP', ''),
              title: title,
              niveau: cert.niveau_europe || cert.niveau,
              secteur: cert.nsf_libelle || cert.nsf_code || 'Non spécifié',
              source: 'api_externe',
              relevanceScore: score,
              isActive: !isInactive,
              blocks: [],
              metadata: cert,
              searchType: cert.searchType // ✅ Conserver le type
            };
          }).sort((a, b) => {
            // Trier par score puis par longueur
            if (b.relevanceScore !== a.relevanceScore) {
              return b.relevanceScore - a.relevanceScore;
            }
            return a.title.length - b.title.length;
          });

          // ✅ Filtrer seulement pour l'affichage principal (score >= 20)
          apiResults = allScoredResults.filter(cert => cert.relevanceScore >= 20);

          console.log('📊 Stats recherche API:', {
            total: apiCerts.length,
            scored: allScoredResults.length,
            filtered: apiResults.length,
            avgScore: apiResults.length > 0
              ? Math.round(apiResults.reduce((sum, c) => sum + c.relevanceScore, 0) / apiResults.length)
              : 0
          });

          // ✅ Afficher le meilleur match avec son TYPE
          if (apiResults.length > 0) {
            const bestMatch = apiResults[0];
            const searchTypeLabel = bestMatch.searchType ? `[TYPE ${bestMatch.searchType}]` : '';
            console.log(`🎯 ${searchTypeLabel} Meilleur match: "${bestMatch.title}" (score: ${Math.round(bestMatch.relevanceScore)}%)`);
          }

          // ✅ Stocker TOUS les résultats scorés dans allCertifications
          setAllCertifications(allScoredResults);
        }
      } catch (apiError) {
        console.warn('API externe non disponible:', apiError.message);
        // Continue avec les résultats DB seulement
      }

      toast.dismiss(loadingToast);

      // Combiner et trier tous les résultats par score
      const combinedResults = [...dbResults, ...apiResults].sort((a, b) => {
        const scoreA = a.relevanceScore || 0;
        const scoreB = b.relevanceScore || 0;
        if (scoreB !== scoreA) return scoreB - scoreA;
        return a.title.length - b.title.length;
      });

      // Enrichir automatiquement les résultats avec 100% de score
      // LIMITE : Seulement les 3 premiers pour éviter les timeouts
      if (combinedResults.length > 0) {
        const perfectMatches = combinedResults.filter(cert =>
          cert.relevanceScore === 100 && cert.source === 'api_externe'
        ).slice(0, 3); // LIMITE : max 3 enrichissements automatiques

        if (perfectMatches.length > 0) {
          console.log(`🔍 Enrichissement de ${perfectMatches.length} résultat(s) avec score 100% (max 3)...`);

          // Enrichir en parallèle
          const enrichmentPromises = perfectMatches.map(async (cert) => {
            try {
              const enrichedResponse = await api.get(`/kit-apprentissage/enriched/${cert.rncpCode}`);

              if (enrichedResponse.data?.data) {
                const enrichedData = enrichedResponse.data.data;
                const blocsCompetences = enrichedData.blocs_competences || [];

                // Créer les blocs de compétences
                const blocks = blocsCompetences.map((bloc, idx) => {
                  const competencesList = (bloc.liste_competences || '').split('\n')
                    .filter(line => line.trim().startsWith('-'))
                    .map(line => line.trim().substring(1).trim());

                  return {
                    id: `api_bloc_${cert.rncpCode}_${idx}`,
                    code: bloc.code || `BLOC${idx + 1}`,
                    intitule: bloc.libelle || bloc.intitule || `Bloc ${idx + 1}`,
                    description: bloc.liste_competences || '',
                    competences: competencesList.map((comp, compIdx) => ({
                      id: `api_comp_${cert.rncpCode}_${idx}_${compIdx}`,
                      code: `${bloc.code}_C${compIdx + 1}`,
                      intitule: comp
                    }))
                  };
                });

                // Mettre à jour le certificat avec les blocs
                cert.blocks = blocks;

                console.log(`  ✅ RNCP ${cert.rncpCode}: ${blocks.length} bloc(s), ${blocks.reduce((sum, b) => sum + b.competences.length, 0)} compétence(s)`);
              }
            } catch (error) {
              console.warn(`  ⚠️ Impossible d'enrichir RNCP ${cert.rncpCode}:`, error.message);
            }
          });

          // Attendre que tous les enrichissements soient terminés
          await Promise.all(enrichmentPromises);
        }

        setCertifications(combinedResults);
        const dbCount = dbResults.length;
        const apiCount = apiResults.length;
        toast.success(
          `${combinedResults.length} certification(s) pertinente(s) (${dbCount} locale, ${apiCount} API)`
        );
      } else {
        setCertifications([]);
        toast.error(`Aucune certification pertinente trouvée pour "${searchTerm}"`);
      }
    } catch (error) {
      console.error('Erreur recherche:', error);
      if (loadingToast) toast.dismiss(loadingToast);
      toast.error('Erreur lors de la recherche');
      setCertifications([]);
    } finally {
      setLoading(false);
    }
  };

  // Récupération des détails d'une certification
  const handleViewDetails = async (cert) => {
    setSelectedCert(cert);
    setLoading(true);

    try {
      // Si les blocs sont déjà enrichis (enrichissement automatique), les utiliser directement
      if (cert.blocks && cert.blocks.length > 0) {
        console.log('✨ Utilisation des blocs déjà enrichis');

        // Extraire toutes les compétences des blocs
        let allCompetences = [];
        cert.blocks.forEach(block => {
          if (block.competences && block.competences.length > 0) {
            const blockCompetences = block.competences.map(comp => ({
              ...comp,
              block_id: block.id,
              block_title: block.intitule
            }));
            allCompetences = [...allCompetences, ...blockCompetences];
          }
        });

        setCertDetails({
          certification: cert,
          blocks: cert.blocks,
          competences: allCompetences
        });
        setViewMode('list');
        toast.success(`Détails affichés (${cert.blocks.length} blocs, ${allCompetences.length} compétences)`);
        setLoading(false);
        return;
      }

      // Si la certification vient de l'API externe mais n'a pas été enrichie, utiliser les métadonnées
      if (cert.source === 'api_externe' && cert.metadata) {
        console.log('📊 Métadonnées API:', cert.metadata);

        // Extraire les blocs de compétences des métadonnées de l'API
        const apiBlocs = cert.metadata.blocs_competences || [];

        let allCompetences = [];
        const blocks = apiBlocs.map((bloc, idx) => {
          // Extraire les compétences de liste_competences (lignes commençant par -)
          const competencesList = (bloc.liste_competences || '').split('\n')
            .filter(line => line.trim().startsWith('-'))
            .map(line => line.trim().substring(1).trim());

          // Créer des objets compétences
          const blocCompetences = competencesList.map((comp, compIdx) => ({
            id: `api_comp_${idx}_${compIdx}`,
            code: `${bloc.code}_C${compIdx + 1}`,
            intitule: comp,
            description: comp,
            block_id: `api_bloc_${idx}`,
            block_title: bloc.libelle || bloc.intitule
          }));

          allCompetences = [...allCompetences, ...blocCompetences];

          return {
            id: `api_bloc_${idx}`,
            code: bloc.code || `BLOC${idx + 1}`,
            intitule: bloc.libelle || bloc.intitule || `Bloc ${idx + 1}`,
            description: bloc.liste_competences || '',
            nb_competences: competencesList.length
          };
        });

        setCertDetails({
          certification: cert,
          blocks: blocks,
          competences: allCompetences
        });
        setViewMode('list');
        toast.success('Détails chargés depuis l\'API externe');
        setLoading(false);
        return;
      }

      // Sinon, chercher dans la base de données
      const response = await api.get(`/rncp/${cert.rncpCode}/details`);

      if (response.data.success) {
        setCertDetails({
          certification: cert,
          blocks: response.data.blocks || [],
          competences: response.data.competences || []
        });
        setViewMode('list');
        toast.success('Détails chargés avec succès');
      } else {
        toast.error('Impossible de charger les détails');
      }
    } catch (error) {
      console.error('Erreur chargement détails:', error);
      toast.error('Erreur lors du chargement des détails');
    } finally {
      setLoading(false);
    }
  };

  // Toggle expansion d'un bloc
  const toggleBlockExpansion = (blockId) => {
    setExpandedBlocks(prev => ({
      ...prev,
      [blockId]: !prev[blockId]
    }));
  };

  // Rendu de la vue graphique hiérarchique
  const renderGraphView = () => {
    if (!certDetails) return null;

    return (
      <div className="space-y-6">
        {/* Nœud Certification (racine) */}
        <div className="flex justify-center">
          <div className="bg-gradient-to-br from-purple-500 to-purple-700 text-white rounded-2xl shadow-2xl p-6 max-w-2xl w-full transform hover:scale-105 transition-transform">
            <div className="flex items-center gap-3 mb-2">
              <Box className="w-8 h-8" />
              <h3 className="text-xl font-bold">Certification RNCP</h3>
            </div>
            <p className="text-lg font-semibold">{certDetails.certification.title}</p>
            <p className="text-purple-200 text-sm mt-1">Code: {certDetails.certification.rncpCode}</p>
          </div>
        </div>

        {/* Ligne de connexion */}
        <div className="flex justify-center">
          <div className="w-1 h-8 bg-gradient-to-b from-purple-400 to-emerald-400"></div>
        </div>

        {/* Blocs de compétences */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {certDetails.blocks.map((block, blockIdx) => {
            const blockCompetences = certDetails.competences.filter(c => c.block_id === block.id);

            return (
              <div key={block.id} className="relative">
                {/* Ligne de connexion vers le haut */}
                <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 w-1 h-8 bg-gradient-to-b from-emerald-400 to-emerald-500"></div>

                {/* Carte du bloc */}
                <div className="bg-gradient-to-br from-emerald-500 to-emerald-700 text-white rounded-xl shadow-xl p-5 transform hover:scale-105 transition-transform">
                  <div className="flex items-center gap-2 mb-3">
                    <Layers className="w-6 h-6" />
                    <h4 className="font-bold text-sm">Bloc de compétences #{blockIdx + 1}</h4>
                  </div>
                  <p className="font-semibold mb-2 line-clamp-2">{block.intitule}</p>
                  <div className="bg-emerald-800 bg-opacity-50 rounded-lg px-3 py-1 text-xs">
                    {blockCompetences.length} compétence(s)
                  </div>

                  {/* Compétences */}
                  {blockCompetences.length > 0 && (
                    <div className="mt-4 space-y-2">
                      <div className="flex justify-center mb-2">
                        <div className="w-1 h-4 bg-gradient-to-b from-emerald-300 to-amber-400"></div>
                      </div>
                      {blockCompetences.map((comp, compIdx) => (
                        <div
                          key={comp.id}
                          className="bg-white text-gray-900 rounded-lg p-3 shadow-md transform hover:scale-105 transition-transform"
                        >
                          <div className="flex items-start gap-2">
                            <Star className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-semibold text-gray-900 line-clamp-2">
                                {comp.intitule || comp.code}
                              </p>
                              {comp.code && comp.intitule && (
                                <p className="text-[10px] text-gray-500 mt-1">{comp.code}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Statistiques */}
        <div className="mt-8 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-6 border border-blue-200">
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-3xl font-bold text-purple-600">1</div>
              <div className="text-sm text-gray-600">Certification</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-emerald-600">{certDetails.blocks.length}</div>
              <div className="text-sm text-gray-600">Blocs</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-amber-600">{certDetails.competences.length}</div>
              <div className="text-sm text-gray-600">Compétences</div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Test RNCP - Certifications & Compétences
        </h1>
        <p className="text-gray-600">
          Recherchez une certification par code RNCP ou intitulé et visualisez ses compétences
        </p>
      </div>

      {/* Zone de recherche */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex gap-4">
          <div className="flex-1">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Ex: 34947 ou CAP Equipier polyvalent du commerce"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <button
            onClick={handleSearch}
            disabled={loading}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <Search className="w-5 h-5" />
            Rechercher
          </button>
        </div>
      </div>

      {/* Résultats de recherche */}
      {certifications.length > 0 && !certDetails && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Résultats de la recherche ({showLowScoreResults ? allCertifications.length : certifications.length})
            {showLowScoreResults && (
              <span className="text-sm text-gray-500 ml-2">(tous scores inclus)</span>
            )}
          </h2>
          <div className="space-y-3">
            {(showLowScoreResults
              ? (showAllResults ? allCertifications : allCertifications.slice(0, 1))
              : (showAllResults ? certifications : certifications.slice(0, 1))
            ).map((cert) => (
              <div
                key={cert.id}
                className={`p-4 border rounded-lg hover:border-blue-500 transition-colors ${
                  cert.source === 'api_externe'
                    ? 'border-orange-200 bg-orange-50'
                    : 'border-green-200 bg-green-50'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <h3 className="font-semibold text-gray-900">{cert.title}</h3>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        cert.source === 'api_externe'
                          ? 'bg-orange-200 text-orange-800'
                          : 'bg-green-200 text-green-800'
                      }`}>
                        {cert.source === 'api_externe' ? 'API externe' : 'Base locale'}
                      </span>
                      {cert.relevanceScore !== undefined && (
                        <span className={`text-xs px-2 py-0.5 rounded-full font-mono ${
                          cert.relevanceScore >= 80 ? 'bg-emerald-200 text-emerald-800' :
                          cert.relevanceScore >= 60 ? 'bg-blue-200 text-blue-800' :
                          cert.relevanceScore >= 40 ? 'bg-yellow-200 text-yellow-800' :
                          'bg-gray-200 text-gray-800'
                        }`}>
                          {Math.round(cert.relevanceScore)}%
                        </span>
                      )}
                      {cert.isActive === false && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-red-200 text-red-800 font-semibold">
                          INACTIF
                        </span>
                      )}
                      {cert.searchType && (
                        <span className="text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-800 font-bold">
                          TYPE {cert.searchType}
                        </span>
                      )}
                    </div>
                    <div className="flex gap-4 mt-2 text-sm text-gray-600">
                      <span className="font-mono bg-white px-2 py-1 rounded border border-gray-300">
                        RNCP {cert.rncpCode}
                      </span>
                      {cert.niveau && (
                        <span>Niveau: {cert.niveau}</span>
                      )}
                      {cert.secteur && (
                        <span>Secteur: {cert.secteur}</span>
                      )}
                    </div>
                    <div className="mt-2 text-sm text-gray-500 flex items-center gap-2">
                      <span>
                        {cert.blocks?.length || 0} bloc(s) de compétences • {' '}
                        {cert.blocks?.reduce((sum, b) => sum + (b.competences?.length || 0), 0) || 0} compétence(s)
                      </span>
                      {cert.blocks && cert.blocks.length > 0 && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-purple-200 text-purple-800 font-semibold">
                          ✨ Enrichi
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => handleViewDetails(cert)}
                    className="ml-4 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 flex items-center gap-2 flex-shrink-0"
                  >
                    <Eye className="w-4 h-4" />
                    Voir les compétences
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Boutons de contrôle */}
          <div className="mt-4 flex flex-col gap-2">
            {/* Bouton "voir plus" (scores >= 20) */}
            {!showLowScoreResults && certifications.length > 1 && !showAllResults && (
              <div className="text-center">
                <button
                  onClick={() => setShowAllResults(true)}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  Voir plus ({certifications.length - 1} autre{certifications.length > 2 ? 's' : ''} résultat{certifications.length > 2 ? 's' : ''})
                </button>
              </div>
            )}

            {/* Bouton "voir moins" */}
            {!showLowScoreResults && certifications.length > 1 && showAllResults && (
              <div className="text-center">
                <button
                  onClick={() => setShowAllResults(false)}
                  className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium"
                >
                  Voir moins
                </button>
              </div>
            )}

            {/* Bouton "Afficher TOUS les résultats (scores faibles inclus)" */}
            {allCertifications.length > certifications.length && !showLowScoreResults && (
              <div className="text-center">
                <button
                  onClick={() => {
                    setShowLowScoreResults(true);
                    setShowAllResults(true);
                  }}
                  className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-medium"
                >
                  Voir TOUS les résultats ({allCertifications.length} total, +{allCertifications.length - certifications.length} avec score &lt; 20)
                </button>
              </div>
            )}

            {/* Bouton retour aux résultats filtrés */}
            {showLowScoreResults && (
              <div className="text-center">
                <button
                  onClick={() => {
                    setShowLowScoreResults(false);
                    setShowAllResults(false);
                  }}
                  className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium"
                >
                  Masquer les résultats à score faible
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Détails de la certification */}
      {certDetails && (
        <div className="bg-white rounded-lg shadow-md p-6">
          {/* En-tête avec infos certification */}
          <div className="mb-6 pb-6 border-b border-gray-200">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  {certDetails.certification.title}
                </h2>
                <div className="flex gap-4 text-sm text-gray-600">
                  <span className="font-mono bg-gray-100 px-3 py-1 rounded">
                    RNCP {certDetails.certification.rncpCode}
                  </span>
                  {certDetails.certification.niveau && (
                    <span className="bg-blue-100 px-3 py-1 rounded">
                      Niveau {certDetails.certification.niveau}
                    </span>
                  )}
                  {certDetails.certification.secteur && (
                    <span className="bg-purple-100 px-3 py-1 rounded">
                      {certDetails.certification.secteur}
                    </span>
                  )}
                </div>
              </div>
              <button
                onClick={() => {
                  setCertDetails(null);
                  setSelectedCert(null);
                  setExpandedBlocks({});
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-900"
              >
                ← Retour à la recherche
              </button>
            </div>

            <div className="flex gap-4 text-lg font-semibold text-gray-700">
              <span className="bg-emerald-100 px-4 py-2 rounded-lg">
                {certDetails.blocks.length} Blocs de compétences
              </span>
              <span className="bg-blue-100 px-4 py-2 rounded-lg">
                {certDetails.competences.length} Compétences
              </span>
            </div>
          </div>

          {/* Toggle vue liste / graphe */}
          <div className="flex gap-2 mb-6">
            <button
              onClick={() => setViewMode('list')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                viewMode === 'list'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <List className="w-5 h-5" />
              Vue Liste
            </button>
            <button
              onClick={() => setViewMode('graph')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                viewMode === 'graph'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Network className="w-5 h-5" />
              Vue Graphique
            </button>
          </div>

          {/* Vue Liste */}
          {viewMode === 'list' && (
            <div className="space-y-4">
              {certDetails.blocks.map((block) => {
                const blockCompetences = certDetails.competences.filter(
                  c => c.block_id === block.id
                );
                const isExpanded = expandedBlocks[block.id];

                return (
                  <div key={block.id} className="border border-gray-200 rounded-lg overflow-hidden">
                    {/* En-tête du bloc */}
                    <div
                      onClick={() => toggleBlockExpansion(block.id)}
                      className="bg-gradient-to-r from-blue-50 to-purple-50 p-4 cursor-pointer hover:from-blue-100 hover:to-purple-100 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            {isExpanded ? (
                              <ChevronDown className="w-5 h-5 text-blue-600" />
                            ) : (
                              <ChevronRight className="w-5 h-5 text-blue-600" />
                            )}
                            <h3 className="font-semibold text-gray-900">
                              {block.intitule}
                            </h3>
                          </div>
                          {block.code && (
                            <p className="text-sm text-gray-600 ml-7 mt-1">
                              Code: {block.code}
                            </p>
                          )}
                          {block.description && (
                            <p className="text-sm text-gray-600 ml-7 mt-1">
                              {block.description}
                            </p>
                          )}
                        </div>
                        <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                          {blockCompetences.length} compétence(s)
                        </span>
                      </div>
                    </div>

                    {/* Compétences du bloc */}
                    {isExpanded && blockCompetences.length > 0 && (
                      <div className="p-4 bg-gray-50">
                        <div className="space-y-2">
                          {blockCompetences.map((comp, idx) => (
                            <div
                              key={comp.id}
                              className="bg-white p-3 rounded-lg border border-gray-200"
                            >
                              <div className="flex items-start gap-3">
                                <span className="flex-shrink-0 w-8 h-8 bg-emerald-100 text-emerald-700 rounded-full flex items-center justify-center text-sm font-semibold">
                                  {idx + 1}
                                </span>
                                <div className="flex-1">
                                  <h4 className="font-medium text-gray-900">
                                    {comp.intitule || comp.code}
                                  </h4>
                                  {comp.code && comp.intitule && (
                                    <p className="text-xs text-gray-500 mt-1">
                                      Code: {comp.code}
                                    </p>
                                  )}
                                  {comp.description && (
                                    <p className="text-sm text-gray-600 mt-2">
                                      {comp.description}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {isExpanded && blockCompetences.length === 0 && (
                      <div className="p-4 bg-gray-50 text-center text-gray-500">
                        Aucune compétence pour ce bloc
                      </div>
                    )}
                  </div>
                );
              })}

              {certDetails.blocks.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  Aucun bloc de compétences disponible
                </div>
              )}
            </div>
          )}

          {/* Vue Graphique */}
          {viewMode === 'graph' && renderGraphView()}
        </div>
      )}

      {/* Message si aucune recherche */}
      {certifications.length === 0 && !certDetails && !loading && (
        <div className="bg-gray-50 rounded-lg p-12 text-center">
          <Search className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-700 mb-2">
            Commencez votre recherche
          </h3>
          <p className="text-gray-600">
            Entrez un code RNCP ou un intitulé de certification pour commencer
          </p>
        </div>
      )}
    </div>
  );
};

export default RncpTest;
